import { Injectable, computed, inject, signal } from '@angular/core';
import { environment } from '@environments/environment';
import { MealMomentModel } from '@models/meal-moment/meal-moment.model';
import { Observable, of, shareReplay, tap } from 'rxjs';
import { HttpService } from './http.service';

const STORAGE_KEY = 'meal_moments_cache';
const STORAGE_KEY_TS = 'meal_moments_cache_ts';
const CACHE_AGE = 60 * 1000 * 5; // 5 minutes
const TICK_INTERVAL = 1000; // 1 seconde

@Injectable({
  providedIn: 'root',
})
export class MealmomentService {
  private readonly mealmomentsApi = `${environment.caisseApiUrl}/meal-moments`;
  private readonly httpService = inject(HttpService);

  private readonly mealMoments = signal<MealMomentModel[]>(this.#readStorage());
  private readonly mealMomentLastUpdate = signal<number>(
    Number(localStorage.getItem(STORAGE_KEY_TS) ?? 0),
  );
  private inFlight$: Observable<MealMomentModel[]> | null = null;
  private readonly tick = signal(Date.now());

  constructor() {
    setInterval(() => this.tick.set(Date.now()), TICK_INTERVAL);
  }

  readonly currentMealMoment = computed(() => {
    const mealmoments = this.mealMoments();
    const now = new Date(this.tick());
    return (
      mealmoments.find((m) => {
        const start = new Date(m.start_time);
        const end = new Date(m.end_time);
        const todayStart = new Date(now);
        todayStart.setHours(start.getHours(), start.getMinutes(), start.getSeconds(), 0);
        const todayEnd = new Date(now);
        todayEnd.setHours(end.getHours(), end.getMinutes(), end.getSeconds(), 0);
        return now >= todayStart && now < todayEnd;
      }) ?? null
    );
  });

  getMealmoments(): Observable<MealMomentModel[]> {
    if (this.mealMomentLastUpdate() > Date.now() - CACHE_AGE) {
      return of(this.mealMoments());
    }
    return this.loadMealMoments();
  }

  loadMealMoments(): Observable<MealMomentModel[]> {
    if (!this.inFlight$) {
      this.inFlight$ = this.httpService.get<MealMomentModel[]>(this.mealmomentsApi).pipe(
        tap((data) => {
          this.mealMoments.set(data);
          this.mealMomentLastUpdate.set(Date.now());
          this.inFlight$ = null;
          localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
          localStorage.setItem(STORAGE_KEY_TS, String(Date.now()));
        }),
        shareReplay(1),
      );
    }
    return this.inFlight$;
  }

  #readStorage(): MealMomentModel[] {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      return raw ? (JSON.parse(raw) as MealMomentModel[]) : [];
    } catch {
      return [];
    }
  }
}
